import React from 'react';
import { JobResumo } from './types';
import { classeStatus, formatarDataHoraCurta, rotuloStatus } from './statusBadge';

const descreverParametros = (parametros: unknown): string | null => {
  if (!parametros || typeof parametros !== 'object') return null;
  const p = parametros as Record<string, unknown>;
  if (p.dataInicial && p.dataFinal) return `${p.dataInicial} a ${p.dataFinal}`;
  if (p.recover) return 'Recovery (reprocessa o relatório de falhas)';
  // ⚠️ O sync de eventos manda `modo` (português), não `mode`: os dois convivem, e ler só
  // `mode` deixaria o período do card em branco em todo job de eventos.
  if (p.modo === 'backfill') {
    return p.desde || p.ate
      ? `Backfill de ${p.desde ?? 'o início'} a ${p.ate ?? 'hoje'}`
      : 'Backfill completo (desde 01/01/2025)';
  }
  if (p.modo === 'incremental') return `Incremental — últimos ${p.dias ?? 30} dias`;
  if (p.mode === 'incremental') return `Incremental desde ${p.desde ?? '?'}`;
  if (p.mode === 'full') return 'Full (backup, truncate e varredura completa)';
  return null;
};

/**
 * Linha rótulo/valor do card. `admin-linha-item` dá o esqueleto e a divisória; o rótulo
 * ganha largura fixa para o valor começar sempre no mesmo ponto (e não ser jogado para a
 * borda oposta do card, que era o que deixava os dois "distantes entre si") e as duas
 * pontas usam `admin-texto-secundario`, distinguidas só pela cor.
 */
export const Linha: React.FC<{ rotulo: string; children: React.ReactNode; title?: string; cor?: string }> = ({
  rotulo,
  children,
  title,
  cor,
}) => (
  <div className="admin-linha-item" style={{ padding: '7px 0', gap: 12, justifyContent: 'flex-start' }}>
    <span className="admin-texto-secundario" style={{ width: 118, flexShrink: 0 }}>
      {rotulo}
    </span>
    <span
      className="admin-texto-secundario"
      style={{ color: cor ?? 'var(--tema-texto, #1A1C1B)', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
      title={title}
    >
      {children}
    </span>
  </div>
);

interface BlocoCardProps {
  titulo: string;
  descricao: string;
  job: JobResumo | null;
  /** Ações do bloco (disparo) — ficam lado a lado com "Ver etapas", no rodapé do card. */
  acoes: React.ReactNode;
  /**
   * Conteúdo extra do card, abaixo das linhas do job. Opcional: renderiza mesmo quando
   * `job` é null — serve para o que descreve o ESTADO DO DADO (ex.: quantos registros
   * estão gravados), que existe independentemente de haver execução registrada.
   */
  extra?: React.ReactNode;
  onVerDetalhe: (jobId: string) => void;
}

/**
 * Card de um bloco de sincronização, no vocabulário das telas de configuração
 * (`admin-card`, `admin-card-title`, `admin-hint`, `admin-linha-item`, `admin-btn`) com o
 * status na paleta compartilhada de badges. Nenhuma classe própria.
 *
 * O card é uma coluna flex e a faixa de ações leva `margin-top: auto`: com blocos que têm
 * quantidades diferentes de informação (o Bloco 2 tem período, o 3 tem resultado), sem
 * isso os botões terminam em alturas distintas e viram uma "escada" entre os cards.
 */
const BlocoCard: React.FC<BlocoCardProps> = ({ titulo, descricao, job, acoes, extra, onVerDetalhe }) => {
  const parametros = job ? descreverParametros(job.parametros) : null;

  return (
    <div className="admin-card" style={{ display: 'flex', flexDirection: 'column', marginBottom: 0 }}>
      <div className="admin-card-title" style={{ justifyContent: 'space-between', marginBottom: 8 }}>
        <span>{titulo}</span>
        {job ? (
          <span className={classeStatus(job.status)}>{rotuloStatus(job.status)}</span>
        ) : (
          <span className="cilia-badge badge-cinza">Nunca executado</span>
        )}
      </div>

      <p className="admin-hint" style={{ margin: 0 }}>
        {descricao}
      </p>

      {job && (
        <div style={{ marginTop: 14 }}>
          <Linha
            rotulo="Última execução"
            title={`${formatarDataHoraCurta(job.startedAt)}${job.finishedAt ? ` até ${formatarDataHoraCurta(job.finishedAt)}` : ''}`}
          >
            {formatarDataHoraCurta(job.startedAt)}
            {job.finishedAt ? ` até ${formatarDataHoraCurta(job.finishedAt)}` : ' (em andamento)'}
          </Linha>

          {parametros && (
            <Linha rotulo="Parâmetros" title={parametros}>
              {parametros}
            </Linha>
          )}

          {job.status === 'RUNNING' && (
            <Linha rotulo="Etapa atual" title={job.etapaAtual ?? undefined}>
              {job.etapaAtual ?? 'iniciando...'} ({job.totalEtapas} etapa(s))
            </Linha>
          )}

          {job.etapasComProblema.length > 0 && (
            <Linha rotulo="Com problema" title={job.etapasComProblema.join(', ')} cor="#dc2626">
              {job.etapasComProblema.join(', ')}
            </Linha>
          )}

          {job.mensagem && (
            <Linha rotulo="Resultado" title={job.mensagem}>
              {job.mensagem}
            </Linha>
          )}

          {job.status === 'RECUPERADO' && job.recuperacao && (
            <Linha rotulo="Recuperação" cor="#16a34a">
              Pendências resolvidas por reprocessamento em {formatarDataHoraCurta(job.recuperacao.em)}
            </Linha>
          )}
        </div>
      )}

      {extra && <div style={{ marginTop: job ? 0 : 14 }}>{extra}</div>}

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 'auto', paddingTop: 16 }}>
        {acoes}
        {job && (
          <button type="button" className="admin-btn admin-btn-secondary" onClick={() => onVerDetalhe(job.id)}>
            Ver etapas
          </button>
        )}
      </div>
    </div>
  );
};

export default BlocoCard;
